import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
export function FAQSection() {
  const faqs = [{
    question: 'How can I pay for my order?',
    answer: 'We accept the following credit cards: Visa, MasterCard, American Express and Discover. We also accept PayPal Express at checkout.'
  }, {
    question: 'Is it safe to use my credit card?',
    answer: 'Absolutely. We provide industry-standard secure checkout through Shopify. Shopify is certified Level 1 PCI DSS compliant and serious about secure hosting and PCI compliance. All account information submitted to us is safely isolated from unauthorized Internet access.'
  }, {
    question: 'Do you have a minimum order quantity (MOQ)?',
    answer: "We don't require a traditional minimum order quantity. Instead, our products are sold by inner packs, which means you'll need to purchase in the pack sizes they come in. For example, if an item is packed in an inner pack of 6, you'll need to order in multiples of 6."
  }, {
    question: 'Do you have a minimum order value?',
    answer: 'Yes. The minimum order value is $1,000 for your first order. After that, there is no minimum, and you may place orders in any amount.'
  }, {
    question: 'How do I apply for a wholesale account?',
    answer: "If you're interested in becoming a wholesale partner, simply complete our application form here: Apply for a Wholesale Account. Once submitted, our team will review your application and get back to you with next steps."
  }, {
    question: 'Which countries do you ship to?',
    answer: "We currently ship within the United States only. However, if you're located outside the U.S., we're happy to ship your order to a freight forwarder of your choice, so you can arrange international delivery through them."
  }, {
    question: 'How are shipping costs calculated?',
    answer: "Shipping costs are determined after your order is placed. Once everything is packed and we have the final weight and dimensions, we'll calculate the shipping cost and provide the details."
  }, {
    question: 'Are your products available year-round or seasonal only?',
    answer: 'All of our products are available year-round, so you can shop our Christmas collections at any time of the year.'
  }, {
    question: 'Can I order samples before placing a bulk order?',
    answer: 'Yes, samples can be ordered before placing a bulk order. Please contact us at wholesale@allseasonsimports.com for assistance with sample requests.'
  }, {
    question: 'How often do you release new designs?',
    answer: "We release new designs at the start of every year, so you'll always have fresh collections to choose from."
  }, {
    question: 'What is your return and refund policy for wholesale orders?',
    answer: 'Please see our detailed Returns Policy for full information on returns and refunds.'
  }, {
    question: 'What should I do if items arrive damaged or defective?',
    answer: 'We do not accept returns or offer refunds for wholesale purchases under any circumstances except when: an item arrives defective, or there are verified damages upon delivery. We stand behind the quality of our products and will replace any item that is found to be defective upon arrival. If your order arrives with any defects or damages: 1) Contact Customer Service Promptly — email the customer service representative who assisted you with your order, or contact us at wholesale@allseasonsimports.com. Please include your order number in the subject line for faster processing. 2) Provide Clear Documentation — you must submit clear photographs of the damaged or defective item(s), including the full item, a close-up of the defect, and the packaging (if the damage is related to shipping). 3) Optional Return Inspection — in some cases, we may ask you to return the item(s) for further inspection. If the defect is confirmed, you will receive a replacement of the same or equivalent item, or a merchandise credit for future wholesale purchases. All decisions regarding replacements or credits are made at our sole discretion.'
  }, {
    question: 'Do you cover shipping costs for returns?',
    answer: 'We do not cover shipping costs for returns unless the items are damaged or defective.'
  }, {
    question: 'Do you provide a catalog or price list in PDF/Excel format?',
    answer: 'Please find attached our 2025 catalog.'
  }, {
    question: 'Can I pick-up my order?',
    answer: 'We offer the ability to pick up orders at our Hialeah, FL location. To arrange for customer pickup, you will need to call our Sales team at 305-557-8799. Located at: 9800 NW 78th Avenue, Dock 3, Hialeah Gardens, FL 33016. Unless directed otherwise, this is the main facility to pick items up at. Your order must be placed online at least 4 hours prior to picking up.'
  }, {
    question: 'Can I get assistance with choosing best-sellers for my store?',
    answer: 'Please contact us at wholesale@allseasonsimports.com for assistance in selecting the best-sellers for your store.'
  }];
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-24 bg-tcp-pure-black border-t border-white/5" data-id="element-15" data-mp="2z8ijn7ldnsn3">
      <div className="container mx-auto px-6 max-w-4xl" data-id="element-16" data-mp="2v6f4o8zi25x2">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-17" data-mp="394c0v4a1kab7">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-18" data-mp="jf1q6ppgswa0">
            Frequently Asked Questions
          </h2>
        </motion.div>

        <div className="space-y-4" data-id="element-19" data-mp="vgt97ezsfy7c">
          {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className={`border ${isOpen ? 'border-tcp-red/50 bg-tcp-black' : 'border-white/10 bg-tcp-pure-black'} transition-colors duration-300`} data-id="element-20" data-mp="10el0q7witib0" data-mpi={index}>
                <button onClick={() => setOpenIndex(isOpen ? null : index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-21" data-mp="3q5zpfx8pnbq7">
                  <span className={`font-semibold text-lg ${isOpen ? 'text-tcp-red' : 'text-white'}`} data-id="element-22" data-mp="3dn44s03xs6uz">
                    {faq.question}
                  </span>
                  <ChevronDownIcon className={`flex-shrink-0 text-gray-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-tcp-red' : ''}`} size={20} data-id="element-23" data-mp="33pzepp6yzr8w" />
                </button>

                <AnimatePresence data-id="element-24" data-mp="2uut8wq7p996d">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-25" data-mp="2pykytr8d0662">
                      <div className="px-6 pb-6 text-gray-400 leading-relaxed" data-id="element-26" data-mp="3pywi2qom665b">
                        {faq.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}